import { FC, ReactNode } from 'react';

import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client';

import { SaleBidderLevel, SaleBidderStatus, SaleStatus } from '__generated__/globalTypes';
import { Button } from 'ui-kit';

import {
  RequestSaleAccess,
  RequestSaleAccessVariables,
} from 'mutation/__generated__/requestSaleAccess';
import { REQUEST_SALE_ACCESS } from 'mutation/requestSaleAccess';

import {
  COMPANY_SALES_QUERY,
  OPEN_SALES_QUERY,
  SALE_ACCESS_QUERY,
  SALE_OFFERINGS_QUERY,
} from 'query/sales';
import { CompanySales, OpenSales, SaleAccessQuery, SaleOfferings } from 'query/__generated__/sales';

import { BidderDataRoom } from './BidderDataRoom';
import { SaleApprovalLine, SaleApprovalPanel } from './SaleApprovalPanel';
import { SaleBidOptimizationPanel } from './SaleBidOptimizationPanel';
import { SaleBidding } from './SaleBidding';
import {
  SaleBiddingClosedSummary,
  SaleBiddingClosedWithoutAccessDocuments,
  SaleFinalBids,
} from './SaleBiddingClosed';
import { SALE_ALLOWS_JOINT_VENTURE_BIDS, SaleOffering } from './saleBids';
import { hasLoanServiceAccessDocuments } from './saleDocuments';
import { SaleOfferingsTable } from './SaleOfferingsTable';
import { SALE_SECTION_LABEL, SaleSection } from './saleSections';
import { SectionPanel, SectionTabs, useSectionParam } from './SectionTabs';
import { SellerDataRoom } from './SellerDataRoom';
import { Acknowledgements, useAccessDocumentGate, useBidNoticeGate } from './useSaleDocumentGates';
import {
  centralTimeParts,
  formatDateTime,
  getBidderAccessEndsAt,
  getBiddingClosedAt,
  getInvitedOpenRound,
  getNextRound,
} from './salesUtils';
import {
  BIDDER_STATUS_LABEL,
  BIDDER_STATUS_PILL,
  PRIMARY_BUTTON_LIGHT_THEME,
  saleStatusBadge,
} from './styles';

type SaleListing = CompanySales['companySales'][number] | OpenSales['openSales'][number];
type SellerSaleListing = Extract<SaleListing, { __typename: 'Sale' }>;
type BidderSaleListing = Extract<SaleListing, { __typename: 'SaleAnnouncement' }>;

const StatCard: FC<{
  label: string;
  value: string;
  testId: string;
  primary?: boolean;
  detail?: string;
}> = ({ label, value, testId, primary, detail }) => (
  <div
    data-testid={testId}
    className={`flex flex-col justify-center gap-1 rounded-lg border p-4 text-center ${
      primary
        ? // The gradient ends on pink-600, not a brighter pink: the gray-100 label has to clear
          // 4.5:1 at every stop, and brighter ends fall to ~3:1.
          'border-[#260626] bg-pink-600 bg-gradient-to-r from-[#1555ec] to-pink-600'
        : 'border-slate-500 border-transparent-0 bg-background-canvas light:border-gray-200 light:bg-gray-100'
    }`}
  >
    <div
      data-testid={`${testId}-value`}
      className={`font-mono text-xl font-bold text-white ${primary ? '' : 'light:text-gray-900'}`}
    >
      {value}
    </div>
    {detail && (
      <div data-testid={`${testId}-detail`} className="text-xs text-gray-400 light:text-gray-700">
        {detail}
      </div>
    )}
    <div
      className={primary ? 'text-sm text-gray-100' : 'text-xs text-gray-400 light:text-gray-700'}
    >
      {label}
    </div>
  </div>
);

const SaleStats: FC<{ sale: SaleListing }> = ({ sale }) => {
  const nextRound = getNextRound(sale.rounds);
  const nextDeadline = nextRound ? centralTimeParts(nextRound.deadline_at, 'numeric') : undefined;
  return (
    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label="Pools"
        testId="sale-stat-pools"
        value={
          'offering_count' in sale ? String(sale.offering_count) : String(sale.offering_ids.length)
        }
        primary
      />
      <StatCard
        label="Next Bid Deadline"
        testId="sale-stat-deadline"
        value={nextDeadline?.central ?? '-'}
        detail={nextDeadline?.local}
      />
      <StatCard
        label="Access Cutoff"
        testId="sale-stat-cutoff"
        value={formatDateTime(sale.access_cutoff_at)}
      />
      <StatCard
        label="Sale Keys"
        testId="sale-stat-sale-keys"
        value={String(sale.sale_keys.length)}
      />
    </div>
  );
};

const AccessStrip: FC<{ sale: BidderSaleListing }> = ({ sale }) => {
  const bidderStatus = sale.bidder_status;
  const accessPillClass = bidderStatus
    ? (BIDDER_STATUS_PILL[bidderStatus] ?? 'bg-slate-700 text-white')
    : 'bg-slate-700 text-gray-200';
  // openSales carries loan-service's level, which has no clock; api ends it at the last deadline.
  const accessPillDetail =
    bidderStatus === SaleBidderStatus.APPROVED && getBiddingClosedAt(sale.rounds)
      ? 'bidding closed'
      : sale.bidder_level;
  return (
    <div className="mt-6 flex items-center justify-between rounded border border-slate-500 border-transparent-0 bg-background-canvas p-4 light:border-gray-200 light:bg-gray-100">
      <div className="text-xs text-gray-400 light:text-gray-700">Your access</div>
      <span
        data-testid="sale-access-pill"
        className={`rounded px-2 py-1 text-center text-xs font-bold ${accessPillClass}`}
      >
        {bidderStatus
          ? `${BIDDER_STATUS_LABEL[bidderStatus] ?? bidderStatus}${accessPillDetail ? ` · ${accessPillDetail}` : ''}`
          : 'No request yet'}
      </span>
    </div>
  );
};

// saleOfferings only needs CA + PEC per its own auth rule, so the pools list shows once a level
// is granted, whether or not this bidder may bid right now.
const useSaleOfferings = (saleId: string) =>
  useQuery<SaleOfferings>(SALE_OFFERINGS_QUERY, { variables: { saleId }, errorPolicy: 'all' });

const OfferingsLoaded: FC<{
  saleId: string;
  children: (offerings: SaleOffering[]) => ReactNode;
}> = ({ saleId, children }) => {
  const { data, loading, error } = useSaleOfferings(saleId);
  if (loading) {
    return <p className="mt-6 text-sm text-gray-400 light:text-gray-700">Loading pools…</p>;
  }
  if (error && !data) {
    return (
      <p className="mt-6 text-sm text-status-error light:text-error-muted">Failed to load pools.</p>
    );
  }
  return <>{children(data?.saleOfferings ?? [])}</>;
};

const SalePools: FC<{ saleId: string; calculationDate: string; isSeller: boolean }> = ({
  saleId,
  calculationDate,
  isSeller,
}) => (
  <OfferingsLoaded saleId={saleId}>
    {(offerings) => (
      <div className="mt-6">
        <h2 className="font-heebo text-lg font-medium text-white light:text-gray-900">Pools</h2>
        <div className="mt-2">
          <SaleOfferingsTable
            saleId={saleId}
            offerings={offerings}
            isSeller={isSeller}
            calculationDate={calculationDate}
          />
        </div>
      </div>
    )}
  </OfferingsLoaded>
);

const BidderBids: FC<{
  sale: BidderSaleListing;
  level: SaleBidderLevel;
  acknowledgements: Acknowledgements;
}> = ({ sale, level, acknowledgements }) => {
  // loan-management only accepts a bid, or the L1 bid-notice acknowledgement, for a round this
  // bidder's company is invited to and that hasn't closed -- without openRound, bidding is closed
  // for this bidder regardless of their access level, so there's nothing to gate behind a notice.
  const openRound = getInvitedOpenRound(sale.rounds);
  const bidNoticeGate = useBidNoticeGate(
    sale.sale_id,
    acknowledgements,
    !!openRound && level === SaleBidderLevel.L1,
  );
  return (
    <OfferingsLoaded saleId={sale.sale_id}>
      {(offerings) => (
        <SaleBidding
          saleId={sale.sale_id}
          round={openRound?.round}
          deadlineAt={openRound?.deadline_at}
          calculationDate={sale.calculation_date}
          offerings={offerings}
          isSeller={false}
          saleAllowsJointVentureBids={SALE_ALLOWS_JOINT_VENTURE_BIDS}
          canBid={!!openRound && !bidNoticeGate}
          noticeGate={bidNoticeGate ?? undefined}
        />
      )}
    </OfferingsLoaded>
  );
};

const SellerBids: FC<{ sale: SellerSaleListing }> = ({ sale }) => {
  const nextRound = getNextRound(sale.rounds);
  return (
    <>
      <OfferingsLoaded saleId={sale.sale_id}>
        {(offerings) => (
          <SaleBidding
            saleId={sale.sale_id}
            round={nextRound?.round}
            deadlineAt={nextRound?.deadline_at}
            calculationDate={sale.calculation_date}
            offerings={offerings}
            isSeller
            saleAllowsJointVentureBids={SALE_ALLOWS_JOINT_VENTURE_BIDS}
            canBid={false}
          />
        )}
      </OfferingsLoaded>
      <SaleBidOptimizationPanel saleId={sale.sale_id} rounds={sale.rounds} />
    </>
  );
};

// Short of a granted level, everything is a gate or an access message on the summary. Only a
// granted level adds the pools, bids and data room tabs; after the deadline, the bids tab alone, for
// a user with both access documents.
type BidderStanding =
  | { kind: 'gated'; summary: ReactNode }
  | { kind: 'biddingClosed'; closedAt: string }
  | { kind: 'granted'; level: SaleBidderLevel; acknowledgements: Acknowledgements };

// The bidder-only gating chain: CA and PEC_SUMMARY, then request access, then a granted level.
// SUMMARY_BID_NOTICE gates only the bid form, on the bids tab.
const useBidderStanding = (sale: BidderSaleListing): BidderStanding => {
  const saleId = sale.sale_id;
  const { data, loading, error } = useQuery<SaleAccessQuery>(SALE_ACCESS_QUERY, {
    variables: { saleId },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const [requestAccess, { loading: requesting }] = useMutation<
    RequestSaleAccess,
    RequestSaleAccessVariables
  >(REQUEST_SALE_ACCESS, {
    refetchQueries: [
      { query: SALE_ACCESS_QUERY, variables: { saleId } },
      { query: OPEN_SALES_QUERY, variables: {} },
      { query: COMPANY_SALES_QUERY, variables: {} },
    ],
  });

  const access = data?.saleAccess;
  const acknowledgements = access?.acknowledgements ?? [];
  const { gate: accessDocumentGate, documentFailedToLoad: accessDocumentFailedToLoad } =
    useAccessDocumentGate(saleId, acknowledgements);

  const gated = (summary: ReactNode): BidderStanding => ({ kind: 'gated', summary });
  const accessMessage = (message: string, isError = false) =>
    gated(
      <p
        data-testid="sale-access-message"
        className={`mt-6 text-sm ${
          isError ? 'text-status-error light:text-error-muted' : 'text-gray-400 light:text-gray-700'
        }`}
      >
        {message}
      </p>,
    );

  if (loading && !data) {
    return gated(<p className="mt-6 text-sm text-gray-400 light:text-gray-700">Loading access…</p>);
  }

  if (error && !access) {
    return gated(
      <p className="mt-6 text-sm text-status-error light:text-error-muted">
        Failed to load your access status.
      </p>,
    );
  }

  const handleRequestAccess = async () => {
    try {
      await requestAccess({ variables: { saleId } });
    } catch (mutationError) {
      toast.error(
        mutationError instanceof Error ? mutationError.message : 'Failed to request access',
      );
    }
  };

  if (!access || !access.status) {
    // loan-service refuses requestSaleAccess once the cut-off passes (sale_bidder.go), so
    // without this the button stays live right up until the click that fails with a raw error
    // toast -- same guard as the sales index's RequestAccessCell.
    if (new Date(sale.access_cutoff_at).getTime() <= Date.now()) {
      return accessMessage(`Access closed on ${formatDateTime(sale.access_cutoff_at)}`);
    }
    const requestAccessDisabled = requesting || !!accessDocumentGate;
    return gated(
      <>
        {accessDocumentGate}
        <div className="mt-6">
          <Button
            size="small"
            type="primary"
            label="Request Access"
            data-testid="sale-request-access"
            disabled={requestAccessDisabled}
            className={requestAccessDisabled ? '' : PRIMARY_BUTTON_LIGHT_THEME}
            aria-describedby={accessDocumentGate ? 'sale-request-access-hint' : undefined}
            onClick={handleRequestAccess}
          >
            Request Access
          </Button>
          {accessDocumentGate && (
            <p
              id="sale-request-access-hint"
              data-testid="sale-request-access-hint"
              className="mt-2 text-xs text-gray-400 light:text-gray-700"
            >
              {accessDocumentFailedToLoad
                ? 'Request Access opens once the documents above load and you agree to them.'
                : 'Agree to the Confidentiality Agreement and the Purchaser Eligibility Certification summary above to request access.'}
            </p>
          )}
        </div>
      </>,
    );
  }

  if (access.status === 'PENDING') {
    return accessMessage('Your access request is pending approval.');
  }
  if (access.status === 'REJECTED') {
    return accessMessage('Your access request was not approved.', true);
  }
  if (access.status === 'REVOKED') {
    return accessMessage('Your access to this sale was revoked.', true);
  }

  // APPROVED from here. Once the last deadline passes api ends the level, and loan-service refuses
  // the CA and PEC acknowledgements too, so neither the gate nor the waiting line applies. A user
  // without the access documents gets nothing past the announcement (SOW §3.8b), so no Bids tab
  // and no saleBids request.
  const biddingClosedAt = getBiddingClosedAt(sale.rounds);
  if (biddingClosedAt) {
    if (hasLoanServiceAccessDocuments(acknowledgements)) {
      return { kind: 'biddingClosed', closedAt: biddingClosedAt };
    }
    return gated(<SaleBiddingClosedWithoutAccessDocuments closedAt={biddingClosedAt} />);
  }
  // Level stays null until the required acknowledgements are in.
  if (accessDocumentGate) {
    return gated(accessDocumentGate);
  }
  if (!access.level) {
    return accessMessage('Waiting on access to open.');
  }
  return { kind: 'granted', level: access.level, acknowledgements };
};

const SALE_SECTIONS_LABEL = 'Sale sections';
const SALE_SECTIONS_ID = 'sale-section';

const salePanel = (section: SaleSection, render: () => ReactNode): SectionPanel<SaleSection> => ({
  section,
  label: SALE_SECTION_LABEL[section],
  render,
});

const SaleSections: FC<{ panels: SectionPanel<SaleSection>[] }> = ({ panels }) => {
  const [section, setSection] = useSectionParam(panels.map((panel) => panel.section));
  return (
    <SectionTabs
      label={SALE_SECTIONS_LABEL}
      idPrefix={SALE_SECTIONS_ID}
      className="mt-6"
      keepHiddenPanelsMounted
      panels={panels}
      active={section}
      onChange={setSection}
    />
  );
};

const BidderSale: FC<{ sale: BidderSaleListing }> = ({ sale }) => {
  const standing = useBidderStanding(sale);
  const panels = [
    salePanel(SaleSection.SUMMARY, () => (
      <>
        <SaleStats sale={sale} />
        <AccessStrip sale={sale} />
        {standing.kind === 'gated' && standing.summary}
        {standing.kind === 'biddingClosed' && (
          <SaleBiddingClosedSummary closedAt={standing.closedAt} />
        )}
      </>
    )),
  ];
  if (standing.kind === 'biddingClosed') {
    panels.push(salePanel(SaleSection.BIDS, () => <SaleFinalBids saleId={sale.sale_id} />));
  }
  if (standing.kind === 'granted') {
    panels.push(
      salePanel(SaleSection.POOLS, () => (
        <SalePools saleId={sale.sale_id} calculationDate={sale.calculation_date} isSeller={false} />
      )),
      salePanel(SaleSection.BIDS, () => (
        <BidderBids
          sale={sale}
          level={standing.level}
          acknowledgements={standing.acknowledgements}
        />
      )),
      salePanel(SaleSection.DOCUMENTS, () => (
        <BidderDataRoom
          saleId={sale.sale_id}
          saleKeys={sale.sale_keys}
          level={standing.level}
          accessEndsAt={getBidderAccessEndsAt(sale.rounds)}
          downloadPeriodHours={sale.download_period_hours}
        />
      )),
    );
  }
  return <SaleSections panels={panels} />;
};

const SellerSale: FC<{ sale: SellerSaleListing }> = ({ sale }) => {
  const panels = [
    salePanel(SaleSection.SUMMARY, () => (
      <>
        <SaleStats sale={sale} />
        {sale.status === SaleStatus.OPEN && <SaleApprovalLine saleId={sale.sale_id} />}
        {sale.status === SaleStatus.PENDING_APPROVAL && <SaleApprovalPanel saleId={sale.sale_id} />}
      </>
    )),
    salePanel(SaleSection.POOLS, () => (
      <SalePools saleId={sale.sale_id} calculationDate={sale.calculation_date} isSeller />
    )),
    salePanel(SaleSection.BIDS, () => <SellerBids sale={sale} />),
  ];
  if (sale.status === SaleStatus.OPEN) {
    panels.push(salePanel(SaleSection.DOCUMENTS, () => <SellerDataRoom saleId={sale.sale_id} />));
  }
  return <SaleSections panels={panels} />;
};

export const SALE_FAILED_COPY = "This sale couldn't be built. Hover for the reason.";

export const SalePage: FC = () => {
  const { sale_id } = useParams<{ sale_id: string }>();

  // openSales only covers sales the caller hasn't necessarily engaged with yet (the browse/
  // request-access list); a seller's own sale, or one the caller is already bidding in, only
  // shows up in companySales. The sale blotter links here from both kinds of row, so both
  // queries are checked.
  const {
    data: openData,
    loading: openLoading,
    error: openError,
  } = useQuery<OpenSales>(OPEN_SALES_QUERY);
  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
  } = useQuery<CompanySales>(COMPANY_SALES_QUERY);

  if (openLoading || companyLoading) {
    return (
      <div
        className="mt-3 h-full min-w-0 animate-pulse rounded-lg bg-background-surface p-6 light:bg-gray-100"
        style={{ gridArea: 'page' }}
      />
    );
  }

  // companySales is checked first: GetOpenSales returns every OPEN sale including the seller's
  // own (as a SaleAnnouncement, same as everyone else's), so a seller whose sale is also open
  // would otherwise match there first and be treated as a bidder (isSeller derives from which
  // list matched, below).
  const sale: SaleListing | undefined =
    companyData?.companySales.find((s) => s.sale_id === sale_id) ??
    openData?.openSales.find((s) => s.sale_id === sale_id);

  // Only escalate to the route error boundary when the sale wasn't found AND a query that
  // could have contained it failed without producing any data -- e.g. a seller's own sale
  // that only lives in companySales still renders fine off a transient openSales failure.
  // Rendering the sale we DID find, even with the other query erroring in the background,
  // beats throwing away good data over an unrelated failure.
  if (!sale && ((openError && !openData) || (companyError && !companyData))) {
    throw openError ?? companyError;
  }

  if (!sale) {
    return (
      <div
        className="mt-3 h-full min-w-0 rounded-lg bg-background-surface p-6 text-white light:bg-white light:text-gray-900"
        style={{ gridArea: 'page' }}
      >
        <h1 data-testid="sale-not-found" className="font-heebo text-lg font-medium">
          Sale not found
        </h1>
      </div>
    );
  }

  const statusBadge = saleStatusBadge(sale);

  return (
    <div
      className="mt-3 h-full min-w-0 rounded-lg bg-background-surface p-6 text-white light:bg-white light:text-gray-900"
      style={{ gridArea: 'page' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1
            data-testid="sale-title"
            className="font-heebo text-2xl font-semibold text-purple-300 light:text-purple-600"
          >
            {sale.sale_name}
          </h1>
          <p className="mt-1 text-sm text-gray-400 light:text-gray-700">
            Sale keys: {sale.sale_keys.join(', ') || '-'}
          </p>
          {sale.status === SaleStatus.FAILED && 'failure_reason' in sale && sale.failure_reason && (
            <p
              className="mt-1 text-sm text-status-error light:text-error-muted"
              title={sale.failure_reason}
            >
              {SALE_FAILED_COPY}
            </p>
          )}
        </div>
        <span
          data-testid="sale-status"
          className={`rounded px-3 py-1.5 text-center text-sm font-bold ${statusBadge.pillClass}`}
        >
          {statusBadge.label}
        </span>
      </div>

      {/* Sale (the seller's own view) has no bidder_status/bidder_level -- only SaleAnnouncement
          (everyone else's view) carries those. Keyed by sale, so no tab or gate state carries
          over when the route moves to another sale. */}
      {'bidder_status' in sale ? (
        <BidderSale key={sale.sale_id} sale={sale} />
      ) : (
        <SellerSale key={sale.sale_id} sale={sale} />
      )}
    </div>
  );
};

export default SalePage;
